import { Play } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { SmoothImage } from "../../primitives/smooth-image";
import { CARD_EYEBROW } from "../../primitives/type";
import type { AttentionMedia } from "./types";

export function AttentionMediaBlock({
  headline,
  media,
  onPlay,
  subtitle,
  size = "md",
}: {
  headline?: string;
  media: AttentionMedia;
  onPlay?: () => void;
  subtitle?: string;
  size?: "sm" | "md" | "hero";
}) {
  const heroUrl = media.thumbnailUrl ?? media.imageUrl ?? null;
  const overlayLabel = formatMediaOverlay(media);
  const surfaceBorder = "color-mix(in srgb, var(--color-paper) 18%, transparent)";
  const isHero = size === "hero";
  const sizeClass = size === "sm" ? "w-[76px]" : isHero ? "w-full" : "w-[128px]";
  const playerSize = size === "sm" ? "h-7 w-7" : isHero ? "h-16 w-16" : "h-9 w-9";
  const playerIconPx = size === "sm" ? 11 : isHero ? 22 : 14;
  // The step ladder is keyed on width, so a wide but short laptop takes the
  // tallest band on the shortest screen. The vh cap is what keeps the resting
  // card inside the viewport there; on tall screens it never binds.
  const heroHeightClass =
    media.kind === "reset" || media.kind === "guide"
      ? "h-[260px] max-h-[30vh] sm:h-[280px] lg:h-[300px] xl:h-[320px]"
      : "h-[220px] max-h-[26vh] sm:h-[240px] lg:h-[260px] xl:h-[280px]";
  const overlayBackground =
    "linear-gradient(to top, color-mix(in srgb, var(--color-navy-deep) 96%, transparent) 0%, color-mix(in srgb, var(--color-navy-deep) 78%, transparent) 38%, color-mix(in srgb, var(--color-navy-deep) 30%, transparent) 68%, transparent 100%), linear-gradient(90deg, color-mix(in srgb, var(--color-navy-deep) 32%, transparent) 0%, transparent 70%)";
  const mediaClassName = cn(
    isHero ? `attention-media-hero ${heroHeightClass}` : "aspect-[4/3] rounded-lg",
    "isolate relative shrink-0 overflow-hidden",
    sizeClass,
  );
  const titleClassName =
    media.kind === "reset" || media.kind === "guide"
      ? "max-w-[14ch] text-[30px] leading-[1.02] sm:text-[38px] lg:text-[44px]"
      : "max-w-[20ch] text-[27px] leading-[1.04] sm:text-[32px] lg:text-[38px]";

  return (
    <div
      className={mediaClassName}
      style={{
        background:
          "linear-gradient(165deg, color-mix(in srgb, var(--color-paper) 12%, transparent) 0%, color-mix(in srgb, var(--color-paper) 5%, transparent) 100%)",
        border: isHero ? 0 : `1px solid ${surfaceBorder}`,
      }}
    >
      {heroUrl ? (
        <SmoothImage src={heroUrl} alt="" frameClassName="absolute inset-0 z-0 bg-transparent" />
      ) : null}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background: overlayBackground,
        }}
      />
      {overlayLabel && size !== "sm" ? (
        <span
          className={cn(
            CARD_EYEBROW,
            "absolute z-[2] rounded-full",
            isHero ? "top-4 right-4 px-3 py-1" : "right-1.5 bottom-1.5 px-1.5 py-0.5",
          )}
          style={{
            background: "color-mix(in srgb, var(--color-navy-deep) 75%, transparent)",
            color: "var(--color-paper)",
          }}
        >
          {overlayLabel}
        </span>
      ) : null}
      {(media.kind === "reset" || media.kind === "guide") && onPlay ? (
        <button
          type="button"
          onClick={onPlay}
          aria-label={media.kind === "guide" ? "Play guide video" : "Play reset video"}
          className={cn(
            "absolute top-1/2 left-1/2 z-[2] grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full backdrop-blur-sm [transition:transform_160ms_var(--ease-out-expo),box-shadow_200ms_var(--ease-out-expo)] hover:scale-[1.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700 active:scale-[0.96]",
            playerSize,
          )}
          style={{
            background: "color-mix(in srgb, var(--color-paper) 92%, transparent)",
            boxShadow:
              "0 20px 45px -20px color-mix(in srgb, var(--color-navy-deep) 72%, transparent)",
            color: "var(--color-teal-700)",
          }}
        >
          <Play size={playerIconPx} weight="fill" aria-hidden="true" />
        </button>
      ) : null}
      {isHero && headline ? (
        <div className="absolute inset-x-0 bottom-0 z-[2] max-w-[900px] px-7 pb-6 pt-16 sm:px-8">
          <h2
            className={cn(
              "m-0 text-balance font-title font-medium tracking-normal text-paper [text-shadow:0_2px_16px_color-mix(in_srgb,var(--color-navy-deep)_90%,transparent)]",
              titleClassName,
            )}
          >
            {headline}
          </h2>
          {subtitle ? (
            <p className={cn(CARD_EYEBROW, "m-0 mt-3 text-paper/80")}>{subtitle}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function mediaSubtitleFor(media: AttentionMedia): string {
  if (media.kind === "reset") return "Breathwork · Guided · Wind-down";
  if (media.kind === "guide") return "Short guide";
  if (media.kind === "story") return "Story · Read";
  return "Recipe · Nutrition";
}

function formatMediaOverlay(media: AttentionMedia): string | null {
  if (
    (media.kind === "reset" || media.kind === "guide") &&
    typeof media.durationSeconds === "number" &&
    media.durationSeconds > 0
  ) {
    const seconds = media.durationSeconds;
    if (seconds < 60) return `${seconds}s`;
    return `${Math.round(seconds / 60)} min`;
  }
  if (media.kind === "guide") return "Short";
  if (media.kind === "story" && typeof media.estimatedReadSeconds === "number") {
    const minutes = Math.max(1, Math.round(media.estimatedReadSeconds / 60));
    return `${minutes} min read`;
  }
  if (media.kind === "recipe") return "Recipe";
  return null;
}
